import { UpdatedImage } from 'novel';
import { Node } from '@tiptap/core';
import { DOMImageResizer } from './dom-image-resizer';

// Function to determine size from style attribute
const getSizeFromStyle = (style?: string): { size: 'small' | 'medium' | 'large' | 'custom', width?: number, height?: number } => {
  
  if (!style) {
    return { size: 'medium' };
  }
  
  // Extract width and height from style
  const widthMatch = style.match(/width:\s*([0-9]+(?:\.[0-9]+)?)px/);
  const heightMatch = style.match(/height:\s*([0-9]+(?:\.[0-9]+)?)px/);
  
  const width = widthMatch ? Math.round(parseFloat(widthMatch[1])) : undefined;
  const height = heightMatch ? Math.round(parseFloat(heightMatch[1])) : undefined;
  
  // Check for predefined sizes (with some tolerance for rounding)
  if (width && Math.abs(width - 300) <= 5) {
    return { size: 'small', width, height };
  }
  if (width && Math.abs(width - 600) <= 5) {
    return { size: 'medium', width, height };
  }
  if (width && Math.abs(width - 900) <= 5) {
    return { size: 'large', width, height };
  }
  
  // If we have dimensions but they don't match predefined sizes, it's custom
  if (width || height) {
    return { size: 'custom', width, height };
  }
  
  // Fallback to medium if no dimensions found
  return { size: 'medium' };
};

// Custom image extension that supports double-click editing
export const EditableImage = UpdatedImage.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      inline: true, // Render images inline within paragraphs to avoid empty paragraph issues
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      'data-size': {
        default: null,
        parseHTML: element => element.getAttribute('data-size'),
        renderHTML: attributes => {
          if (!attributes['data-size']) return {};
          return { 'data-size': attributes['data-size'] };
        },
      },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const container = document.createElement('div');
      container.className = 'image-container not-prose';
      
      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      if (node.attrs.class) img.className = node.attrs.class;
      if (node.attrs.style) img.style.cssText = node.attrs.style;

      let domResizer: DOMImageResizer | null = null;
      let isSelected = false;
      let isCurrentlyResizing = false;

      // Function to show the resizer
      const showResizer = () => {
        if (isSelected) {
          return;
        }
        
        isSelected = true;
        container.classList.add('resizing');
        
        // Create the DOM resizer if not already created
        if (!domResizer) {
          const currentWidth = img.offsetWidth || 300;
          const currentHeight = img.offsetHeight || 200;
          
          
          domResizer = new DOMImageResizer({
            initialWidth: currentWidth,
            initialHeight: currentHeight,
            onResizeStart: () => {
              isCurrentlyResizing = true;
            },
            onResize: (width: number, height: number) => {
              // Update image size during resize
              img.style.width = `${width}px`;
              img.style.height = `${height}px`;
            },
            onResizeEnd: (width: number, height: number) => {
              // Set the image to exactly match the resizer border dimensions
              img.style.width = `${width}px`;
              img.style.height = `${height}px`;
              
              // Update the editor state when resize is complete
              const pos = getPos();
              if (typeof pos === 'number') {
                const mergedAttrs = {
                  ...node.attrs,
                  width,
                  height,
                  style: `width: ${width}px; height: ${height}px;`
                };
                
                const tr = editor.state.tr.setNodeMarkup(pos, undefined, mergedAttrs);
                editor.view.dispatch(tr);
              }
              
              // Keep isCurrentlyResizing = true briefly to block mouseup clicks
              // Then allow normal click-to-hide behavior
              setTimeout(() => {
                isCurrentlyResizing = false;
              }, 50); // Just long enough to let mouseup events pass
            }
          });
          
          container.appendChild(domResizer.getElement());
        } else {
          // Update the resizer size if it already exists
          const currentWidth = img.offsetWidth || 300;
          const currentHeight = img.offsetHeight || 200;
          domResizer.updateSize(currentWidth, currentHeight);
        }
        
        // Show the resizer
        domResizer.getElement().style.display = 'block';
      };

      // Function to hide the resizer
      const hideResizer = () => {
        if (!isSelected) {
          return;
        }
        
        if (isCurrentlyResizing) {
          return;
        }
        
        isSelected = false;
        container.classList.remove('resizing');
        
        if (domResizer) {
          domResizer.getElement().style.display = 'none';
        }
      };

      // On image load, ensure aspect ratio is correct if only width is provided
      img.onload = () => {
        if (img.style.width && !img.style.height) {
          const naturalWidth = img.naturalWidth;
          const naturalHeight = img.naturalHeight;
          if (naturalWidth > 0 && naturalHeight > 0) {
            const aspectRatio = naturalHeight / naturalWidth;
            const currentWidth = parseFloat(img.style.width);
            const newHeight = Math.round(currentWidth * aspectRatio);
            img.style.height = `${newHeight}px`;

            // Persist this change to the editor state
            const pos = getPos();
            if (typeof pos === 'number') {
              const newStyle = `width: ${currentWidth}px; height: ${newHeight}px;`;
              const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                style: newStyle,
              });
              editor.view.dispatch(tr);
            }
          }
        }
      };
      // Add click handler to show resizer
      img.addEventListener('click', (e) => {
        e.stopPropagation();
        showResizer();
      });

      // Add double-click handler for editing
      img.addEventListener('dblclick', () => {
        // Get the size from the live DOM element's style, not the stale node attributes
        const sizeInfo = getSizeFromStyle(img.style.cssText);
        
        // Get current position and fetch the latest node from editor state
        const position = typeof getPos === 'function' ? getPos() : getPos;
        
        // Try multiple approaches to get the current node data
        let currentSrc = node.attrs.src; // fallback
        let currentAlt = node.attrs.alt || '';
        
        // Approach 1: Try to get fresh node from editor state
        if (typeof position === 'number' && editor && editor.state) {
          const freshNode = editor.state.doc.nodeAt(position);
          if (freshNode && freshNode.type.name === 'image') {
            currentSrc = freshNode.attrs.src;
            currentAlt = freshNode.attrs.alt || '';
          }
        }
        
        // Approach 2: Use DOM values as backup (most reliable for what's actually displayed)
        if (img.src && img.src !== currentSrc) {
          currentSrc = img.src;
        }
        if (img.alt !== currentAlt) {
          currentAlt = img.alt;
        }
        
        const imageData = {
          url: currentSrc,
          alt: currentAlt,
          size: sizeInfo.size,
          width: sizeInfo.width,
          height: sizeInfo.height
        };
        
        // Dispatch custom event with image data
        const event = new CustomEvent('edit-image', {
          detail: { imageData, position }
        });
        document.dispatchEvent(event);
      });
      
      // Add hover effect (only when not resizing)
      img.addEventListener('mouseenter', () => {
        if (!isSelected) {
          img.style.cursor = 'pointer';
          img.style.opacity = '0.8';
        }
      });
      
      img.addEventListener('mouseleave', () => {
        if (!isSelected) {
          img.style.cursor = 'default';
          img.style.opacity = '1';
        }
      });

      // Hide resizer when clicking outside
      const handleDocumentClick = (e: Event) => {
        if (!container.contains(e.target as HTMLElement)) {
          hideResizer();
        }
      };
      document.addEventListener('click', handleDocumentClick);
      
      container.appendChild(img);
      
      return {
        dom: container,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'image') return false;
          
          // Update the node reference to keep it current
          node = updatedNode;
          
          // Only update src and alt if they're different to avoid unnecessary changes
          if (updatedNode.attrs.src && updatedNode.attrs.src !== img.src) {
            img.src = updatedNode.attrs.src;
          }
          if (updatedNode.attrs.alt !== undefined && updatedNode.attrs.alt !== img.alt) {
            img.alt = updatedNode.attrs.alt || '';
          }
          if (updatedNode.attrs.class) {
            img.className = updatedNode.attrs.class;
          }
          
          // Apply style updates, but preserve any manual resizing
          if (updatedNode.attrs.style) {
            // Parse the style to extract width and height
            const styleWidth = updatedNode.attrs.style.match(/width:\s*([0-9]+(?:\.[0-9]+)?)px/);
            const styleHeight = updatedNode.attrs.style.match(/height:\s*([0-9]+(?:\.[0-9]+)?)px/);
            
            if (styleWidth && styleHeight) {
              const width = parseFloat(styleWidth[1]);
              const height = parseFloat(styleHeight[1]);
              
              img.style.width = `${width}px`;
              img.style.height = `${height}px`;
              
              // Update the resizer to match if it exists
              if (domResizer) {
                domResizer.updateSize(width, height);
              }
            } else {
              // Apply the full style if no specific dimensions found
              img.style.cssText = updatedNode.attrs.style;
            }
          }
          
          return true;
        },
        destroy: () => {
          // Clean up event listeners to prevent memory leaks
          document.removeEventListener('click', handleDocumentClick);
          
          // Clean up DOM resizer if it exists
          if (domResizer) {
            domResizer.destroy?.();
          }
        }
      };
    };
  },
});
